import React from 'react';

import { formatDate, formatMontant } from '../../lib/formats';
import { texteSur } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { StatutBadge } from '../ui/StatutBadge';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import { CelluleMontant } from './paiementsUi';

/**
 * « Acomptes » : les paiements déjà versés au partenaire et pas encore
 * imputés. Un acompte coché se déduit du net à payer, pour un montant au plus
 * égal à son disponible.
 *
 * `lecture` : le décompte est validé ou annulé, la liste est celle qu'il porte.
 */
export const AcomptesDecompte = ({
  acomptes,
  saisie,
  devise = 'XOF',
  disabled = false,
  lecture = false,
  onBasculer,
  onMontant,
}) => {
  if (!acomptes || acomptes.length === 0) return null;
  const total = acomptes.reduce((somme, acompte) => {
    const valeur = lecture ? acompte.amount : saisie.avances[acompte.payment_id];
    return somme + (Number(valeur) || 0);
  }, 0);

  return (
    <section className="space-y-2" aria-label="Acomptes" data-testid="decompte-acomptes">
      <h2 className="text-sm font-bold text-foreground">Acomptes</h2>
      <div className="overflow-x-auto rounded-lg border border-border/70">
        <Table stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              {!lecture ? <TableHead className="w-8" /> : null}
              <TableHead>Date</TableHead>
              <TableHead>Observations</TableHead>
              {!lecture ? <TableHead className="text-right">Disponible</TableHead> : null}
              <TableHead className="text-right">Déduit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {acomptes.map((acompte) => {
              const choisi = lecture || saisie.avances[acompte.payment_id] !== undefined;
              return (
                <TableRow
                  key={acompte.payment_id}
                  data-state={choisi && !lecture ? 'selected' : undefined}
                  data-testid={`decompte-acompte-${acompte.payment_id}`}
                >
                  {!lecture ? (
                    <TableCell>
                      <Checkbox
                        checked={choisi}
                        disabled={disabled}
                        onCheckedChange={() => onBasculer(acompte)}
                        aria-label={`Déduire l’acompte du ${formatDate(acompte.date)}`}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap tabular-nums">{formatDate(acompte.date)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{texteSur(acompte.note, '')}</TableCell>
                  {!lecture ? <CelluleMontant valeur={acompte.available} devise={devise} /> : null}
                  <TableCell className="text-right">
                    {lecture ? (
                      <span className="whitespace-nowrap tabular-nums">{formatMontant(acompte.amount, devise)}</span>
                    ) : choisi ? (
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max={acompte.available}
                        step="any"
                        value={saisie.avances[acompte.payment_id]}
                        disabled={disabled}
                        onChange={(evenement) => onMontant(acompte, evenement.target.value)}
                        aria-label={`Montant déduit de l’acompte du ${formatDate(acompte.date)}`}
                        className="ml-auto h-8 w-32 text-right tabular-nums"
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="font-bold">
              <TableCell colSpan={lecture ? 2 : 4}>Total déduit</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums" data-testid="decompte-total-acomptes">
                {formatMontant(total, devise)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </section>
  );
};

/**
 * « Règlements » d'un décompte validé : ce qui a payé sa facture en plus des
 * acomptes déduits (paiements du reste, depuis cette page ou l'écran Argent).
 */
export const ReglementsDecompte = ({ reglements, devise = 'XOF' }) => {
  if (!reglements || reglements.length === 0) return null;
  const total = reglements.reduce((somme, reglement) => somme + (Number(reglement.amount) || 0), 0);
  return (
    <section className="space-y-2" aria-label="Règlements" data-testid="decompte-reglements">
      <h2 className="text-sm font-bold text-foreground">Règlements</h2>
      <div className="overflow-x-auto rounded-lg border border-border/70">
        <Table stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Observations</TableHead>
              <TableHead className="text-right">Montant</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reglements.map((reglement) => (
              <TableRow key={reglement.payment_id} data-testid={`decompte-reglement-${reglement.payment_id}`}>
                <TableCell className="whitespace-nowrap tabular-nums">{formatDate(reglement.date)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{texteSur(reglement.note, '')}</TableCell>
                <CelluleMontant valeur={reglement.amount} devise={devise} />
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className="font-bold">
              <TableCell colSpan={2}>Total réglé</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums" data-testid="decompte-total-reglements">
                {formatMontant(total, devise)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </section>
  );
};

const Ligne = ({ libelleLigne, valeur, devise, signe = '', testid }) => (
  <div className="flex items-baseline justify-between gap-6">
    <dt className="text-sm text-muted-foreground">{libelleLigne}</dt>
    <dd className="whitespace-nowrap text-sm tabular-nums" data-testid={testid}>
      {signe}
      {formatMontant(valeur, devise)}
    </dd>
  </div>
);

/**
 * Le bas du décompte : total des soldes − acomptes déduits = net à payer. Pour
 * un décompte validé, le paiement se lit sur sa facture (`bill`) : payé, payé
 * en partie, ou à payer avec ce qui reste dû.
 */
export const RecapDecompte = ({ totaux, bill = null, devise = 'XOF', apercu = false }) => (
  <section
    className="ml-auto w-full max-w-sm space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3"
    aria-label="Récapitulatif du décompte"
    data-testid="decompte-recap"
  >
    <dl className="space-y-1">
      <Ligne libelleLigne="Total des soldes" valeur={totaux.solde} devise={devise} testid="recap-soldes" />
      <Ligne
        libelleLigne="Acomptes déduits"
        valeur={totaux.avances}
        devise={devise}
        signe={totaux.avances ? '− ' : ''}
        testid="recap-acomptes"
      />
    </dl>
    <div className="flex items-baseline justify-between gap-6 border-t border-border pt-2">
      <p className="text-sm font-semibold">Net à payer</p>
      <p
        className={cn('kpi-value whitespace-nowrap tabular-nums', apercu && 'opacity-80')}
        data-testid="recap-net"
      >
        {formatMontant(totaux.net, devise)}
      </p>
    </div>
    {bill && bill.payment_status ? (
      <div className="flex items-center justify-between gap-3 border-t border-border pt-2" data-testid="recap-paiement">
        <StatutBadge domaine="statut_paiement_etat" code={bill.payment_status} />
        {bill.payment_status !== 'PAYE' ? (
          <span className="whitespace-nowrap text-sm tabular-nums">Reste {formatMontant(bill.remaining, devise)}</span>
        ) : null}
      </div>
    ) : null}
  </section>
);
